import React from 'react';
import '../embed.css';

import LMCSimulator from './LMCSimulator';
import PacketSwitchingSimulator from './PacketSwitchingSimulator';
import TowersOfHanoiSimulator from './TowersOfHanoiSimulator';
import FractalDrawingSimulator from './FractalDrawingSimulator';
import DiffusionDenoisingSimulator from './DiffusionDenoisingSimulator';
import GANLoopSimulator from './GANLoopSimulator';
import ConditionalGenerationSimulator from './ConditionalGenerationSimulator';
import VAELatentSpaceSimulator from './VAELatentSpaceSimulator';
import FlowReversibilitySimulator from './FlowReversibilitySimulator';
import LogicGateAdventure from './LogicGateAdventure';

/*
 * EmbedSimulator — the chrome-less, anonymous, embeddable surface for the public
 * simulators. Mounted directly from index.js when the path is /embed/sim/<slug>,
 * so the full app shell (auth, nav, theme system) never loads.
 *
 * The simulators are plain client-side React components with no server state, so
 * (unlike the brookebits code embed) there is nothing to fetch and no token: the
 * URL carries only the simulator slug, which matches its public standalone route
 * (/packs -> /embed/sim/packs, /caverns -> /embed/sim/caverns, and so on).
 *
 * Every simulator renders its "Back" button only when an onBack prop is passed
 * (`{onBack && ...}`), so mounting one here WITHOUT onBack gives a clean, chrome-
 * less panel automatically — nothing in the simulators themselves changes.
 *
 * Framing is allowed on the InThinking eBook (thinkib.net) and brookebit itself
 * by the /embed/ exemption in the backend's security-headers handler; see
 * backend/app/main.py (_security_headers) and TEACHER_PLAN_SPEC / the simulator
 * embed note. Admin generates the <iframe> snippet from the /admin page.
 */

// Slug -> component. Keyed by the public route slug so an embed URL mirrors the
// standalone page the admin already knows. Keep this in step with the public
// simulator routes in App.js.
const SIMULATORS = {
  cpus:        LMCSimulator,
  packs:       PacketSwitchingSimulator,
  hanoi:       TowersOfHanoiSimulator,
  fractals:    FractalDrawingSimulator,
  diffusion:   DiffusionDenoisingSimulator,
  gan:         GANLoopSimulator,
  conditional: ConditionalGenerationSimulator,
  latent:      VAELatentSpaceSimulator,
  reversible:  FlowReversibilitySimulator,
  caverns:     LogicGateAdventure,
};

export default function EmbedSimulator() {
  // /embed/sim/<slug> -> "<slug>" (tolerate a trailing slash and any ?query).
  const after = window.location.pathname.replace(/\/+$/, '').split('/embed/sim/')[1] || '';
  const slug = after.split('/')[0].toLowerCase();
  const Simulator = SIMULATORS[slug];

  if (!Simulator) {
    return (
      <div className="embed-root embed-centered">
        <div className="embed-message">
          <p>This simulator isn’t available.</p>
          <a href="https://brookebit.com" target="_blank" rel="noreferrer">brookebit.com</a>
        </div>
      </div>
    );
  }

  // No onBack -> no back button -> chrome-less. The simulator fills the iframe.
  return <Simulator />;
}

// Exported so the admin embed panel and tests share one source of truth for the
// available slugs.
export const EMBEDDABLE_SIMULATORS = Object.keys(SIMULATORS);
